import { useId, useRef, useState, type ReactNode } from "react";
import { CheckIcon, ChevronDownIcon, PlusIcon } from "lucide-react";

import { cn } from "./lib/cn";
import { Button } from "./ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { ButtonGroup, ButtonGroupText } from "./ui/button-group";
import { Popover, PopoverAnchor, PopoverContent } from "./ui/popover";

/** Value reserved for the always-visible new-thread row, so it can never collide with a thread id. */
const NEW_THREAD_VALUE = "__ml-ai-new-thread__";

export type ThreadSwitcherItem = {
  id: string;
  title: string;
  /** Trailing metadata such as a relative age. */
  meta?: string;
  /** Fully composed accessible name; falls back to the title. */
  ariaLabel?: string;
};

export type ThreadSwitcherGroup = {
  id: string;
  /** Omitted for the leading group so recent threads need no redundant heading. */
  heading?: string;
  items: readonly ThreadSwitcherItem[];
};

export type ThreadSwitcherLabels = {
  /** Accessible name of the trigger, describing what the control does. */
  trigger: string;
  /** Accessible name of the popover region. */
  history: string;
  /** Search field placeholder and accessible name. */
  search: string;
  newThread: string;
  /** Shown when the account has no threads at all. */
  empty: string;
  /** Shown when a search matches no thread. */
  searchEmpty: string;
};

export type ThreadSwitcherProps = {
  groups: readonly ThreadSwitcherGroup[];
  activeId?: string;
  /** Visible trigger text; normally the active thread title. */
  activeTitle: string;
  labels: ThreadSwitcherLabels;
  search: string;
  onSearchChange: (search: string) => void;
  onSelect: (id: string) => void;
  onNewThread: () => void;
  className?: string;
  /** Applied to the title button so host applications can keep their existing test hooks. */
  triggerTestId?: string;
  /** Applied to the menu button that sits beside the title. */
  menuTestId?: string;
  /** Renders one compact menu button instead of the default title-and-menu pair. */
  singleTrigger?: boolean;
  /**
   * False when the title names no real conversation yet, so it renders as a label rather than a
   * second opener. Defaults to true.
   */
  isTitleInteractive?: boolean;
  /** Optional host-owned title control, such as an inline rename field. */
  titleControl?: ReactNode;
};

/**
 * Find, resume, or start a conversation from one shadcn combobox.
 *
 * The component is deliberately data-free: grouping and filtering happen in the caller, so
 * `cmdk` filtering stays off and the list renders exactly the groups it was given. Only the
 * popover's own open state lives here, because nothing outside this control depends on it.
 */
export const ThreadSwitcher = ({
  groups,
  activeId,
  activeTitle,
  labels,
  search,
  onSearchChange,
  onSelect,
  onNewThread,
  className,
  triggerTestId,
  menuTestId,
  singleTrigger = false,
  isTitleInteractive = true,
  titleControl,
}: ThreadSwitcherProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLButtonElement>(null);
  const wasEscapeDismissedRef = useRef(false);
  const historyId = useId();
  const hasThreads = groups.some((group) => group.items.length > 0);
  /*
   * `cmdk` highlights the first row in DOM order and re-picks it on every search change, so a
   * leading new-conversation row would swallow the Enter key that should open the top match.
   * Ordering, not a controlled highlight, is what decides this: results lead once a query
   * narrows them, and the new-conversation row leads when there is nothing to match.
   */
  const isSearching = search.length > 0;

  const close = () => {
    setIsOpen(false);
    onSearchChange("");
  };
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setIsOpen(true);
      return;
    }
    close();
  };
  const handleNewThread = () => {
    close();
    onNewThread();
  };
  const handleSelect = (id: string) => {
    close();
    if (id !== activeId) {
      onSelect(id);
    }
  };

  const newThreadRow = (
    <>
      {isSearching && <CommandSeparator />}
      <CommandGroup forceMount>
        <CommandItem forceMount onSelect={handleNewThread} value={NEW_THREAD_VALUE}>
          <PlusIcon aria-hidden="true" />
          {labels.newThread}
        </CommandItem>
      </CommandGroup>
      {!isSearching && <CommandSeparator />}
    </>
  );

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen}>
      {/*
       * The title and the menu affordance are two buttons in one group, so each is its own hover
       * and focus target. That leaves the popover with two openers rather than one, which is why
       * the group is an anchor and the buttons drive the open state themselves. The anchor keeps
       * its own element rather than `asChild`: slotting it onto the group would overwrite the
       * group's `data-slot` with the anchor's.
       *
       * No ButtonGroupSeparator: that is for borderless variants. With `outline`, the group's own
       * `border-l-0` rule makes the two buttons share one border, and adding a separator on top of
       * it draws a second line.
       */}
      <PopoverAnchor>
        {singleTrigger ? (
          <Button
            ref={menuRef}
            aria-controls={isOpen ? historyId : undefined}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-label={labels.trigger}
            className={cn("max-w-64 justify-start font-normal", className)}
            data-slot="thread-switcher-menu"
            data-testid={triggerTestId}
            onClick={() => handleOpenChange(!isOpen)}
            size="sm"
            variant="outline"
          >
            <span className="min-w-0 truncate">{activeTitle}</span>
            <ChevronDownIcon aria-hidden="true" className="opacity-60" />
          </Button>
        ) : (
          <ButtonGroup className={className}>
            {titleControl ??
              (isTitleInteractive ? (
                <Button
                  ref={triggerRef}
                  aria-controls={isOpen ? historyId : undefined}
                  aria-expanded={isOpen}
                  aria-haspopup="dialog"
                  aria-label={labels.trigger}
                  className="max-w-64 justify-start font-normal"
                  data-slot="thread-switcher-title"
                  data-testid={triggerTestId}
                  onClick={() => handleOpenChange(!isOpen)}
                  size="sm"
                  variant="outline"
                >
                  <span className="min-w-0 truncate">{activeTitle}</span>
                </Button>
              ) : (
                <ButtonGroupText
                  className="max-w-64 px-3 font-normal"
                  data-slot="thread-switcher-title"
                  data-testid={triggerTestId}
                >
                  <span className="min-w-0 truncate">{activeTitle}</span>
                </ButtonGroupText>
              ))}
            <Button
              ref={menuRef}
              aria-controls={isOpen ? historyId : undefined}
              aria-expanded={isOpen}
              aria-haspopup="dialog"
              aria-label={labels.history}
              data-slot="thread-switcher-menu"
              data-testid={menuTestId}
              onClick={() => handleOpenChange(!isOpen)}
              size="icon-sm"
              variant="outline"
            >
              <ChevronDownIcon aria-hidden="true" className="opacity-60" />
            </Button>
          </ButtonGroup>
        )}
      </PopoverAnchor>
      <PopoverContent
        align="start"
        aria-label={labels.history}
        className="w-[min(360px,calc(100vw-2rem))] p-0"
        data-slot="thread-switcher-content"
        id={historyId}
        // Keyboard dismissal must land back on the control the keyboard came from. Radix restores
        // whatever held focus before the popover opened, which is not the trigger when the popover
        // was opened by pointer, so the escape path claims focus itself.
        onCloseAutoFocus={(event) => {
          if (!wasEscapeDismissedRef.current) {
            return;
          }
          wasEscapeDismissedRef.current = false;
          event.preventDefault();
          // The title is a plain label when it names no conversation, so the menu is the only
          // control the keyboard could have arrived from.
          (singleTrigger || !isTitleInteractive || titleControl
            ? menuRef.current
            : triggerRef.current
          )?.focus();
        }}
        onEscapeKeyDown={() => {
          wasEscapeDismissedRef.current = true;
        }}
      >
        <Command label={labels.history} shouldFilter={false}>
          <CommandInput
            aria-label={labels.search}
            onValueChange={onSearchChange}
            placeholder={labels.search}
            value={search}
          />
          <CommandList className="max-h-[min(420px,calc(100dvh-8rem))]">
            {!isSearching && newThreadRow}
            {hasThreads ? (
              groups.map((group) =>
                group.items.length === 0 ? null : (
                  <CommandGroup heading={group.heading} key={group.id}>
                    {group.items.map((item) => (
                      <CommandItem
                        aria-current={item.id === activeId ? "page" : undefined}
                        aria-label={item.ariaLabel ?? item.title}
                        data-active={item.id === activeId ? "true" : undefined}
                        key={item.id}
                        onSelect={() => handleSelect(item.id)}
                        value={item.id}
                      >
                        <CheckIcon
                          aria-hidden="true"
                          className={cn(item.id === activeId ? "opacity-100" : "opacity-0")}
                        />
                        <span className="min-w-0 flex-1 truncate">{item.title}</span>
                        {item.meta && (
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {item.meta}
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ),
              )
            ) : (
              /*
               * `cmdk` filtering is off, so its own empty slot never fires; the caller's search
               * text is what separates "nothing yet" from "nothing matched".
               */
              <p className="px-3 py-6 text-center text-sm text-muted-foreground" role="status">
                {search.length > 0 ? labels.searchEmpty : labels.empty}
              </p>
            )}
            {isSearching && newThreadRow}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
